// Admin › Roles & Permissions: each role's permissions by module, and the pages of the menu they open (as
// they are ticked). A department's role (Sales, Assembly, Service, GRP, Accounting, Warehouse, the Moderator)
// has a standard access, every page of its own section of the menu (shared rbac.ts DEPARTMENT_STANDARD):
// when some of it is ticked off, the page says which pages that shuts, and Restore standard access ticks it
// back on. (The start-up step _rbac_v20 restored them once on the live roles.)
import { useEffect, useMemo, useState } from 'react';
import { DEPARTMENT_STANDARD, permissionLabel, type Permission } from '@technohull/shared';
import { NAV, type NavItem } from '../../app/nav.js';
import { ActionBar, ErrorState, InlineError, LoadingState, PageHeader, useConfirm } from '../../components/ui.js';
import {
  useCreateRole,
  useDeleteRole,
  useRoles,
  useSetRolePermissions,
  type RoleRecord,
} from '../../features/admin.js';
import { AdminBack } from './AdminHub.js';
import { fold, hasText } from '../../lib/search.js';

export function AdminRoles() {
  const { data, isLoading, error, refetch } = useRoles();
  const setPerms = useSetRolePermissions();
  const createRole = useCreateRole();
  const deleteRole = useDeleteRole();
  const [confirmNode, confirm] = useConfirm();

  const [selected, setSelected] = useState<string | null>(null);
  const [local, setLocal] = useState<Set<string>>(new Set());
  // the role `local` holds the ticks of (they load a render after the role is picked)
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [permQ, setPermQ] = useState('');

  const roles = data?.roles ?? [];
  const current: RoleRecord | undefined = roles.find((r) => r.name === selected);

  // default selection + load its permissions locally when the role changes.
  useEffect(() => {
    if (!selected && roles.length) setSelected(roles[0]!.name);
  }, [roles, selected]);
  useEffect(() => {
    if (current) { setLocal(new Set(current.permissions)); setLoadedFor(current.name); }
  }, [current?.name, current?.permissions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // how many permissions differ from the saved role (ticked on + ticked off)
  const changes = useMemo(() => {
    if (!current) return 0;
    const saved = new Set(current.permissions);
    return current.permissions.filter((p) => !local.has(p)).length + [...local].filter((p) => !saved.has(p)).length;
  }, [current, local]);
  const dirty = changes > 0;

  const toggle = (id: string) => setLocal((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = () => { if (current) setPerms.mutate({ role: current.name, permissions: [...local] }); };
  const discard = () => { if (current) setLocal(new Set(current.permissions)); };

  // switching role reloads the matrix from the saved role, so unsaved ticks would vanish silently: ask first
  const pick = async (name: string) => {
    if (name === selected) return;
    if (dirty && current) {
      const next = roles.find((r) => r.name === name)?.label ?? name;
      const ok = await confirm({
        title: 'Discard changes?',
        message: `${changes} unsaved change${changes === 1 ? '' : 's'} to “${current.label}” will be lost if you switch to “${next}”.`,
        confirmLabel: 'Discard and switch',
        cancelLabel: 'Keep editing',
        danger: true,
      });
      if (!ok) return;
    }
    setSelected(name);
  };

  const doCreate = () => {
    const name = newName.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
    if (!name || !newLabel.trim()) return;
    createRole.mutate({ name, label: newLabel.trim() }, {
      onSuccess: () => { setCreating(false); setNewName(''); setNewLabel(''); setSelected(name); },
    });
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <AdminBack />
      <PageHeader title="Roles & Permissions">
        <button className="btn btn-sm btn-primary" onClick={() => setCreating((c) => !c)}>+ Create role</button>
      </PageHeader>

      {creating && (
        <div className="page-panel">
          <div className="adm-form">
            <label>
              <span className="field-label">Key</span>
              <input className="field" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="e.g. quality_control" value={newName} onChange={(e) => setNewName(e.target.value)} />
            </label>
            <label>
              <span className="field-label">Display name</span>
              <input className="field" placeholder="e.g. Quality Control" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
            </label>
            <div className="adm-form-acts">
              <button className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={createRole.isPending || !newName.trim() || !newLabel.trim()} onClick={doCreate}>Create role</button>
            </div>
          </div>
          {createRole.isError && <InlineError error={createRole.error} onDismiss={() => createRole.reset()} />}
        </div>
      )}

      <div className="roles-layout">
        <nav className="roles-list" aria-label="Roles">
          {roles.map((r) => (
            <button
              key={r.name}
              type="button"
              aria-current={selected === r.name ? 'true' : undefined}
              className={`roles-list-item ${selected === r.name ? 'active' : ''}`}
              onClick={() => void pick(r.name)}
            >
              <span>{r.label}</span>
              <span className="muted" style={{ fontSize: 10 }}>{r.is_system ? 'system' : `${r.permissions.length}`}</span>
            </button>
          ))}
        </nav>

        <div className="roles-detail">
          {/* ≤800px: the role list folds into this picker, pinned while the matrix scrolls */}
          {current && (
            <label className="roles-picker">
              <span className="field-label">Role</span>
              <select className="field" value={current.name} onChange={(e) => void pick(e.target.value)}>
                {roles.map((r) => (
                  <option key={r.name} value={r.name}>{r.label} · {r.is_system ? 'system' : `${r.permissions.length} permissions`}</option>
                ))}
              </select>
            </label>
          )}
          {current && (
            <>
              <div className="page-head" style={{ marginBottom: 8 }}>
                <div>
                  <h2 className="adm-panel-title" style={{ fontSize: 16 }}>{current.label}</h2>
                  <div className="muted">{current.description || current.name}{current.is_system ? ' · system role' : ''}</div>
                </div>
                <div className="spacer" />
                {!current.is_system && (
                  <button className="btn btn-sm btn-danger" disabled={deleteRole.isPending} onClick={async () => { if (await confirm({ title: 'Delete role', message: `Delete role “${current.label}”?`, confirmLabel: 'Delete', danger: true })) deleteRole.mutate(current.name, { onSuccess: () => setSelected(null) }); }}>Delete role</button>
                )}
                <button className="btn btn-sm btn-primary" disabled={!dirty || setPerms.isPending} onClick={save}>{setPerms.isPending ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}</button>
              </div>
              {current.name === 'administrator' && <p className="muted" style={{ marginBottom: 8 }}>The Admin role always has full access.</p>}
              {current.name !== 'administrator' && loadedFor === current.name && (
                <RolePages
                  role={current.name}
                  perms={local}
                  onRestore={(missing) => setLocal((s) => new Set([...s, ...missing]))}
                />
              )}

              <div className="toolbar roles-filter" style={{ marginBottom: 10 }}>
                <input className="field" aria-label="Filter permissions" placeholder="Filter permissions…" value={permQ} onChange={(e) => setPermQ(e.target.value)} />
                <span className="muted">{local.size} selected</span>
              </div>

              {(data?.modules ?? []).map((mod) => {
                const query = fold(permQ.trim());
                const perms = (data?.catalog ?? []).filter(
                  (c) => c.module === mod && hasText(query, c.label, mod),
                );
                if (!perms.length) return null;
                const allOn = perms.every((p) => local.has(p.id));
                const anyOn = perms.some((p) => local.has(p.id));
                const setAll = (on: boolean) => setLocal((s) => { const n = new Set(s); for (const p of perms) on ? n.add(p.id) : n.delete(p.id); return n; });
                return (
                  <div key={mod} className="perm-module">
                    <div className="perm-module-hd" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span>{mod}</span>
                      <span style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-ghost" onClick={() => setAll(true)} disabled={allOn}>All</button>
                        <button className="btn btn-sm btn-ghost" onClick={() => setAll(false)} disabled={!anyOn}>None</button>
                      </span>
                    </div>
                    <div className="perm-grid">
                      {perms.map((p) => (
                        <label key={p.id} className="perm-switch">
                          <input type="checkbox" checked={local.has(p.id)} onChange={() => toggle(p.id)} />
                          <span>{p.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
              {setPerms.isError && <InlineError error={setPerms.error} onDismiss={() => setPerms.reset()} />}
              {deleteRole.isError && <InlineError error={deleteRole.error} onDismiss={() => deleteRole.reset()} />}
            </>
          )}
        </div>
      </div>
      {confirmNode}
      {/* phones: the save sits under the thumb, however far down the matrix the last tick was */}
      <ActionBar show={dirty} message={`${changes} unsaved`}>
        <button className="btn btn-ghost" disabled={setPerms.isPending} onClick={discard}>Discard</button>
        <button className="btn btn-primary" disabled={setPerms.isPending} onClick={save}>{setPerms.isPending ? 'Saving…' : 'Save changes'}</button>
      </ActionBar>
    </>
  );
}

const leaves = (items: readonly NavItem[]): NavItem[] => items.flatMap((i) => (i.children ? leaves(i.children) : [i])).filter((i) => i.path && !i.hidden);

/** the pages of the menu a set of permissions opens, section by section; for a department's role, its
 *  standard access and what is missing of it */
function RolePages({ role, perms, onRestore }: { role: string; perms: ReadonlySet<string>; onRestore: (missing: Permission[]) => void }) {
  const has = (p: Permission) => perms.has(p);
  const opens = (i: NavItem) => (i.anyPerm ? i.anyPerm.some(has) : !i.perm || has(i.perm));
  const sections = NAV.filter((g) => g.title !== 'Main')
    .map((g) => ({ title: g.title, pages: !g.perm || has(g.perm) ? leaves(g.items).filter((i) => i.perm || i.anyPerm).filter(opens) : [] }))
    .filter((g) => g.pages.length);
  const std = DEPARTMENT_STANDARD[role];
  const missing = std ? std.permissions.filter((p) => !has(p)) : [];
  const own = std ? NAV.find((g) => g.title === std.section) : undefined;
  // its own section, and the parts of others it opens too (Configuration, or its Sales submenu)
  const also = (std?.also ?? []).map((name) => {
    const [title, sub] = name.split(' › ');
    const g = NAV.find((x) => x.title === title);
    return { name, items: (sub ? g?.items.find((i) => i.label === sub)?.children : g?.items) ?? [] };
  });
  const where = std ? [std.section, ...also.map((a) => a.name)] : [];
  const shut = missing.length
    ? [
        ...(own ? leaves(own.items).filter((i) => !opens(i) || (own.perm != null && !has(own.perm))) : []),
        ...also.flatMap((a) => leaves(a.items).filter((i) => !opens(i))),
      ].map((i) => i.label)
    : [];
  const sectionsText = where.length > 1 ? `${where.slice(0, -1).join(', ')} and ${where[where.length - 1]}` : where[0];
  return (
    <section className="roles-pages" aria-label="Pages this role opens">
      {std && (
        <p className={`roles-std${missing.length ? ' is-short' : ''}`}>
          {missing.length ? (
            <>
              <b>Standard access incomplete.</b> The {std.section} department should open every page of {sectionsText}; without{' '}
              {missing.map(permissionLabel).join(', ')} it can’t open {shut.join(', ') || 'all of them'}.{' '}
              <button type="button" className="btn btn-sm btn-primary" onClick={() => onRestore(missing)}>Restore standard access</button>
            </>
          ) : (
            <>Standard access: every page of <b>{sectionsText}</b>.</>
          )}
        </p>
      )}
      <div className="roles-pages-hd">Pages this role opens</div>
      {sections.length ? (
        <ul className="roles-pages-list">
          {sections.map((g) => (
            <li key={g.title}><span className="roles-pages-sec">{g.title}</span> {g.pages.map((i) => i.label).join(' · ')}</li>
          ))}
        </ul>
      ) : <p className="muted">Only the pages everyone has (Explore, Dashboard, Calendar).</p>}
    </section>
  );
}
